import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  Typography,
  Snackbar,
  Alert,
  InputAdornment,
  Divider,
  Chip,
} from '@mui/material';
import { User, X, Check, Eraser, Search as SearchIcon, Calendar } from 'lucide-react';
import api from '../services/api';

// ─── helper: calcula a data yyyy-MM-dd de uma célula dado weekStart + weekday ─
const getCellDateKey = (weekStartStr, weekdayStr) => {
  if (!weekStartStr || !weekdayStr) return null;
  const baseDate = new Date(weekStartStr + 'T00:00:00');
  const dayIndexMap = {
    MONDAY: 0, TUESDAY: 1, WEDNESDAY: 2, THURSDAY: 3,
    FRIDAY: 4, SATURDAY: 5, SUNDAY: 6,
  };
  const offset = dayIndexMap[weekdayStr.toUpperCase()] ?? 0;
  const cellDate = new Date(baseDate);
  cellDate.setDate(baseDate.getDate() + offset);
  const yyyy = cellDate.getFullYear();
  const mm   = String(cellDate.getMonth() + 1).padStart(2, '0');
  const dd   = String(cellDate.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ─── helper: formata intervalo da semana para exibição ───────────────────────
const formatWeekDisplay = (weekStart) => {
  if (!weekStart) return '';
  const date    = new Date(weekStart + 'T00:00:00');
  const endDate = new Date(date);
  endDate.setDate(date.getDate() + 6);
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${date.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', opts)}`;
};

// ─────────────────────────────────────────────────────────────────────────────

const ScheduleCellModal = ({ open, hotel, day, weekStart, isCurrentWeek, onClose, onSave }) => {
  const [employees,    setEmployees]    = useState([]);
  const [selected,     setSelected]     = useState({});
  const [startTimes,   setStartTimes]   = useState({});
  const [endTimes,     setEndTimes]     = useState({});
  const [hotelId,      setHotelId]      = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError,    setShowError]    = useState(false);
  const [search,       setSearch]       = useState('');
  const [offsForThisDay, setOffsForThisDay] = useState([]);  // nomes com OFF aprovado

  // ── Carregamento de dados ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      try {
        // Todas as chamadas em paralelo — schedule_weeks já é a fonte única
        const [empRes, hotelRes, scheduleRes, offsRes] = await Promise.all([
          api.get('/employees'),
          api.get('/hotels'),
          // week_start é sempre passado; o backend lê diretamente de schedule_weeks
          api.get('/schedule/week', { params: { week_start: weekStart } }),
          api.get('/requests/off/approved'),
        ]);

        // Funcionários ativos
        const ativos = empRes.data.filter((emp) => emp[6] === 'active');
        setEmployees(ativos);

        // Hotel atual
        const foundHotel = hotelRes.data.find((h) => h.name === hotel);
        setHotelId(foundHotel?.id ?? null);

        // Quem já está escalado nesse hotel/dia
        const dayKey = day.toUpperCase();
        const scheduleForHotel = scheduleRes.data?.schedule?.[hotel]?.[dayKey] || [];

        const preSelected  = {};
        const preStartTimes = {};
        const preEndTimes   = {};

        scheduleForHotel.forEach(({ name, start, end }) => {
          const employee = ativos.find((emp) => emp[1] === name);
          if (employee) {
            const id = employee[0];
            preSelected[id]   = true;
            preStartTimes[id] = start || '10:45';
            preEndTimes[id]   = end   || '17:00';
          }
        });

        setSelected(preSelected);
        setStartTimes(preStartTimes);
        setEndTimes(preEndTimes);
        setSearch('');

        // OFF aprovados para esse dia específico
        const cellDateKey = getCellDateKey(weekStart, day);
        const offsList = (offsRes.data || [])
          .filter((off) => (off.request_date || '').slice(0, 10) === cellDateKey)
          .map((off) => off.employee_name);

        setOffsForThisDay(offsList);
      } catch (err) {
        console.error('❌ Erro ao carregar dados:', err);
      }
    };

    loadData();
  }, [open, hotel, day, weekStart]);

  // ── Ações ──────────────────────────────────────────────────────────────────
  const toggleEmployee = (id) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleSave = async () => {
    try {
      const formattedDay = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();

      const payload = Object.keys(selected)
        .filter((id) => selected[id])
        .map((id) => ({
          employee_id: id,
          hotel_id:    hotelId,
          day_of_week: formattedDay,
          start_time:  startTimes[id] || '10:45',
          end_time:    endTimes[id]   || '17:00',
        }));

      if (payload.length === 0) {
        // limpar a célula inteira
        await api.delete('/schedule/week', {
          params: { hotel_id: hotelId, day_of_week: formattedDay, week_start: weekStart },
        });
      } else {
        // salvar — week_start sempre no query string
        await api.post('/schedule/week', payload, {
          params: { week_start: weekStart },
        });
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('❌ Erro ao salvar schedule:', err);
      setErrorMessage(err?.response?.data?.msg || 'Erro ao salvar agendamento');
      setShowError(true);
    }
  };

  const handleClear = async () => {
    try {
      const formattedDay = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
      await api.delete('/schedule/week', {
        params: { hotel_id: hotelId, day_of_week: formattedDay, week_start: weekStart },
      });
      onSave();
      onClose();
    } catch (err) {
      console.error('❌ Erro ao limpar schedule:', err);
    }
  };

  // ── Lista filtrada + agrupada por letra ───────────────────────────────────
  const filteredAndGrouped = useMemo(() => {
    const byName = (a, b) =>
      String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: 'base' });

    const filtered = employees
      .filter((e) =>
        !search.trim() ||
        String(e[1]).toLowerCase().includes(search.trim().toLowerCase())
      )
      .sort(byName);

    const groups = {};
    filtered.forEach((emp) => {
      const letter = String(emp[1] || '').charAt(0).toUpperCase();
      const key    = /[A-Z]/.test(letter) ? letter : '#';
      if (!groups[key]) groups[key] = [];
      groups[key].push(emp);
    });

    const orderedKeys = Object.keys(groups).sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });

    return { groups, orderedKeys };
  }, [employees, search]);

  const isEmpOffThatDay = (empName) => offsForThisDay.includes(empName);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {/* Título */}
      <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>{`${hotel} - ${day}`}</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={16} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {formatWeekDisplay(weekStart)}
              </Typography>
            </Box>
            {isCurrentWeek && (
              <Chip label="Current Week" color="primary" size="small" />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Busca */}
        <Box sx={{ p: 2, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee..."
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider />

        {/* Lista de funcionários */}
        <Box sx={{ p: 2, maxHeight: 520, overflow: 'auto' }}>
          {filteredAndGrouped.orderedKeys.map((letter) => (
            <Box key={letter} sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  px: 1.5, py: 0.5, mb: 1,
                  fontWeight: 700,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                {letter}
              </Typography>

              <FormGroup>
                {filteredAndGrouped.groups[letter].map((emp) => {
                  const empId      = emp[0];
                  const empName    = emp[1];
                  const isOff      = isEmpOffThatDay(empName);

                  return (
                    <Box key={empId} sx={{ mb: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selected[empId]}
                            onChange={() => toggleEmployee(empId)}
                            disabled={isOff}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <User size={16} />
                            <Typography
                              sx={{
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                opacity: isOff ? 0.5 : 1,
                              }}
                            >
                              {empName}
                              {isOff && (
                                <Chip
                                  size="small"
                                  color="error"
                                  label="OFF"
                                  sx={{ height: 18, fontSize: '0.65rem', lineHeight: 1 }}
                                />
                              )}
                            </Typography>
                          </Box>
                        }
                      />

                      {selected[empId] && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                          <TextField
                            label="Start"
                            type="time"
                            value={startTimes[empId] || '10:45'}
                            onChange={(e) =>
                              setStartTimes((prev) => ({ ...prev, [empId]: e.target.value }))
                            }
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            disabled={isOff}
                          />
                          <TextField
                            label="End"
                            type="time"
                            value={endTimes[empId] || '17:00'}
                            onChange={(e) =>
                              setEndTimes((prev) => ({ ...prev, [empId]: e.target.value }))
                            }
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            disabled={isOff}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </FormGroup>
            </Box>
          ))}

          {filteredAndGrouped.orderedKeys.length === 0 && (
            <Typography sx={{ color: 'text.secondary' }}>No employees found.</Typography>
          )}
        </Box>
      </DialogContent>

      {/* Snackbar de erro */}
      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShowError(false)} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Rodapé */}
      <Box sx={{ px: 3, pb: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          onClick={handleClear}
          startIcon={<Eraser size={18} />}
          sx={{
            fontWeight: 'bold',
            backgroundColor: 'rgb(255, 255, 255)',
            color: 'rgb(255, 60, 60)',
            '&:hover': { backgroundColor: 'rgb(255, 60, 60)', color: '#fff' },
          }}
        >
          Limpar
        </Button>

        <Button
          onClick={onClose}
          startIcon={<X size={18} />}
          sx={{
            fontWeight: 'bold',
            backgroundColor: '#f5f5f5',
            '&:hover': { backgroundColor: '#e0e0e0' },
          }}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Check size={18} />}
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(to right, #1976d2, #0d47a1)',
            color: '#fff',
            '&:hover': { background: 'linear-gradient(to right, #1565c0, #003c8f)' },
          }}
        >
          Salvar
        </Button>
      </Box>
    </Dialog>
  );
};

export default ScheduleCellModal;